import {hasOwn} from '@pexip/utils';

import {diagnosticEndpoint, diagnosticMethod} from './diagnosticEndpoint';
import {logger, redact} from './logger';
import type {CreateFetcher, Logger} from './types';

const debugLogger: Logger = {
    log: (meta, msg) => logger.debug(meta, msg),
    redact: (text: string) => logger.redact(text),
};
const infoLogger: Logger = {
    log: (meta, msg) => logger.info(meta, msg),
    redact: (text: string) => logger.redact(text),
};

/**
 * Fetch that logs each request and response by its method, endpoint template
 * and status only. The URL, its query, the request body and the response
 * body are never logged; an unrecognized URL is omitted. The token and
 * conference name of a JSON response are registered for redaction before the
 * response is returned.
 *
 * @example
 * const fetchWithLog = createFetcherWithLogging(infoLogger);
 * await fetchWithLog(`${host}/api/client/v2/conferences/${alias}/request_token`, {method: 'POST'});
 * // logs {method: 'POST', endpoint: '/api/client/v2/conferences/{conferenceAlias}/request_token'}
 */
const createFetcherWithLogging: CreateFetcher = log => async (input, init) => {
    const url = input instanceof Request ? input.url : input.toString();
    const method = diagnosticMethod(
        init?.method ?? (input instanceof Request ? input.method : 'GET'),
    );
    const request = {method, endpoint: diagnosticEndpoint(url)};
    log.log(request, 'Request');
    const response = await window.fetch(input, init);
    if (
        response.headers.get('content-type')?.startsWith('application/json') &&
        response.status !== 204
    ) {
        try {
            const body = (await response.clone().json()) as unknown;
            if (body && hasOwn(body, 'result')) {
                redact(body.result, 'token');
                redact(body.result, 'conference_name');
            }
        } catch {
            // Not JSON after all; there is nothing to register.
        }
    }
    log.log({...request, status: response.status}, 'Response');
    return response;
};

export const fetchWithDebugLog = createFetcherWithLogging(debugLogger);
export const fetchWithInfoLog = createFetcherWithLogging(infoLogger);
